import React, { useEffect } from "react";

import Stack from "@mui/material/Stack";

import { useAppSelector } from "app/hooks";
import FirstActionTracker from "components/first-action-tracker";
import MentionsLaunchController from "components/mentions/launch/mentions-launch-controller.component";
import SourceViewerDeepLink from "components/source-record/source-viewer-deep-link.component";
import QuickLauncher from "features/app-layout/quick-launcher";
import { BookingDeskFlagSync } from "features/app-layout/scheduler-choice/booking-desk-flag-sync.component";
import { OpportunityFieldFlagSync } from "features/app-layout/scheduler-choice/opportunity-field-flag-sync.component";
import { PRE_AUTH_USER_ID, selectCurrentUser } from "features/auth/auth-slice";
import MissedCallNotifications from "features/browser-notifications/missed-call-notifications.component";
import VoiceHistoryNotifications from "features/browser-notifications/voice-history-notifications.component";
import DialstackSoftphoneWidget from "features/calls/dialstack/softphone/dialstack-softphone-widget.component";
import { DialstackSoftphoneProvider } from "features/calls/dialstack/softphone/use-dialstack-softphone.hook";
import ScribePurchaseReturn from "features/medical-scribe/components/scribe-purchase-return.component";
import { usePatientDrawer } from "features/patients/patient-drawer";
import { AlertBarProvider } from "hooks/use-alert-bar.hook";
import useAuth from "hooks/use-authorize.hook";
import { PrivacyModeProvider } from "hooks/use-patient-privacy.hook";
import { TouchpointsCountProvider } from "hooks/use-touchpoints-count";
import { useLazyGetTextTemplatesQuery } from "services/text-templates";

import BootstrapErrorScreen from "./bootstrap-error-screen.component";
import LayoutMainContent from "./layout-main-content";
import LayoutSideMenu from "./layout-side-menu";

export default function Layout() {
  const { isOpen: isPatientDrawerOpen } = usePatientDrawer();
  const currentUser = useAppSelector(selectCurrentUser);

  const { bootstrapFailureCount, bootstrapErrorMessage, retryBootstrap } =
    useAuth();

  // preload text template names to use in WordProcessor field
  const [triggerLazyGetTextTemplatesQuery] = useLazyGetTextTemplatesQuery();
  useEffect(() => {
    triggerLazyGetTextTemplatesQuery({
      query: "{id,name,type,category}"
    });
  }, [triggerLazyGetTextTemplatesQuery]);

  // ENG-1745: the bootstrap /me fetch has failed at least once and no user
  // has ever loaded for this token — show an actionable error instead of
  // rendering the shell against the pre-auth placeholder, which previously
  // read as an indefinite spinner while every user-dependent query on the
  // page stayed unresolved. Gated on PRE_AUTH_USER_ID so a *later* re-fetch
  // failure (token refresh, practice switch) never tears down an
  // already-working session.
  if (bootstrapFailureCount > 0 && currentUser.id === PRE_AUTH_USER_ID) {
    return (
      <BootstrapErrorScreen
        message={bootstrapErrorMessage}
        onRetry={retryBootstrap}
      />
    );
  }

  return (
    <FirstActionTracker actionName="main_app">
      <TouchpointsCountProvider>
        <Stack
          className="app-layout"
          direction="row"
          height="100%"
          sx={{
            width: "100vw",
            height: "100vh"
          }}
        >
          <LayoutSideMenu />
          <AlertBarProvider>
            <PrivacyModeProvider>
              <DialstackSoftphoneProvider>
                <VoiceHistoryNotifications />
                <MissedCallNotifications />
                <LayoutMainContent isDrawerOpen={isPatientDrawerOpen} />
                {/* Renders nothing (and binds no hotkey) while the
                    `quickLauncher` flag is off. Above the router outlet so ⌘K
                    works on every page, but inside PrivacyModeProvider (the
                    context chip shows initials in privacy mode) and
                    DialstackSoftphoneProvider ("Call patient" would otherwise
                    hit the provider's inert fallback and silently do nothing). */}
                <QuickLauncher />
                <BookingDeskFlagSync />
                <OpportunityFieldFlagSync />
                <SourceViewerDeepLink />
                {/* Team @mentions launch hints; renders nothing with the flag off. */}
                <MentionsLaunchController />
                {/* Stripe's return from a Scribe purchase, on whichever route it started. */}
                <ScribePurchaseReturn />
                <DialstackSoftphoneWidget />
              </DialstackSoftphoneProvider>
            </PrivacyModeProvider>
          </AlertBarProvider>
        </Stack>
      </TouchpointsCountProvider>
    </FirstActionTracker>
  );
}
